<template>
  <div class="iv-action-panel">
    <div class="iv-action-head">
      <h2 class="iv-action-title">Action required</h2>
      <span v-if="timeframeLabel" class="iv-action-tf">{{ timeframeLabel }}</span>
    </div>
    <p class="iv-action-desc">
      High-priority issues that may lead to claim rejections, billing delays, or a poor patient
      experience. Click one to filter the list.
    </p>

    <div v-if="chips.length" class="iv-action-grid">
      <button
        v-for="chip in chips"
        :key="chip.code"
        type="button"
        class="iv-action-chip"
        :class="{ 'iv-action-chip--active': chip.active }"
        :style="chipStyle(chip)"
        @click="onSelect(chip.code)"
      >
        <span class="iv-action-chip-icon" :style="{ color: chip.color }">
          <i class="fa" :class="chip.icon" aria-hidden="true"></i>
        </span>
        <span class="iv-action-chip-label">{{ chip.label }}</span>
        <span class="iv-action-chip-count" :style="{ color: chip.color }">{{ chip.count }}</span>
      </button>
    </div>

    <div v-else class="iv-action-clear">
      <i class="fa fa-check-circle" aria-hidden="true"></i>
      No coverage issues in this range.
    </div>

    <!-- OD data issues: the per-code chips above already filter the list, so this
         says the ONE thing the chips can't — that these cost nothing. A refused
         check is screened before any carrier call (lib/preflight.js), so staff
         read "unable to verify" as a data-entry task, not as money burned. -->
    <div v-if="dataIssuesCount > 0" class="iv-datafix">
      <span class="iv-datafix-icon"><i class="fa fa-id-card-o" aria-hidden="true"></i></span>
      <div class="iv-datafix-text">
        <div class="iv-datafix-title">
          {{ dataIssuesCount }}
          {{ dataIssuesCount === 1 ? 'appointment needs' : 'appointments need' }}
          an Open Dental fix before verifying
        </div>
        <div class="iv-datafix-sub">
          These are skipped for free — no check is sent or billed until the record is corrected.
        </div>
      </div>
    </div>

    <!-- Unmapped carriers: a data-quality nudge, not a row filter. The payer
         mapping UI to resolve these is Slice 6c — surfaced here read-only. -->
    <div v-if="unmappedCarriers.length" class="iv-unmapped">
      <span class="iv-unmapped-icon"><i class="fa fa-random" aria-hidden="true"></i></span>
      <div class="iv-unmapped-text">
        <div class="iv-unmapped-title">
          {{ unmappedTotal }} {{ unmappedTotal === 1 ? 'carrier needs' : 'carriers need' }} payer mapping
        </div>
        <div class="iv-unmapped-sub">
          {{ unmappedNames }} won't auto-verify until mapped to a payer ID.
        </div>
      </div>
    </div>

    <!--
      Carriers the office chose to exclude. NOT a problem state — grey, not amber — but it
      MUST be visible: an exclusion is invisible money-saving, and an office that forgets it
      excluded a carrier reports "verification is broken" rather than "I excluded that".
    -->
    <div v-if="excludedCarriers.length" class="iv-excluded-note">
      <span class="iv-excluded-icon"><i class="fa fa-ban" aria-hidden="true"></i></span>
      <div class="iv-unmapped-text">
        <div class="iv-unmapped-title">
          {{ excludedTotal }} {{ excludedTotal === 1 ? 'carrier is' : 'carriers are' }} excluded from electronic verification
        </div>
        <div class="iv-unmapped-sub">
          {{ excludedNames }} will not be checked. Verify by phone, or change this in Precheck settings.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {
  CHIP_ORDER,
  chipForCode,
  chipColors,
} from './constants.js';

export default {
  name: 'ActionRequiredPanel',
  props: {
    // Map of warning-code → count from the dashboard payload (`chipCounts`).
    chipCounts: {
      type: Object,
      default: () => ({}),
    },
    // Distinct unmapped carriers: [{ carrierNum, carrierName, count }].
    unmappedCarriers: {
      type: Array,
      default: () => [],
    },
    // Distinct excluded carriers, same shape. Absent on an older backend -> empty -> no banner.
    excludedCarriers: {
      type: Array,
      default: () => [],
    },
    // Total appointment rows blocked by the OD preflight screen. The per-code
    // breakdown arrives inside `chipCounts`, so this is only the headline number.
    dataIssuesCount: {
      type: Number,
      default: 0,
    },
    // The currently-active issue filter code (or null).
    activeIssue: {
      type: String,
      default: null,
    },
    timeframeLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    chips() {
      const counts = this.chipCounts || {};
      // Known codes in canonical order, then any unexpected codes after.
      const codes = [
        ...CHIP_ORDER,
        ...Object.keys(counts).filter((c) => !CHIP_ORDER.includes(c)),
      ];
      const seen = new Set();
      const out = [];
      codes.forEach((code) => {
        if (seen.has(code)) return;
        seen.add(code);
        const count = counts[code] || 0;
        if (count <= 0) return;
        const def = chipForCode(code);
        const colors = chipColors(def.severity);
        out.push({
          code,
          count,
          label: def.label,
          icon: def.icon,
          color: colors.color,
          bg: colors.bg,
          border: colors.border,
          active: this.activeIssue === code,
        });
      });
      return out;
    },
    unmappedTotal() {
      return this.unmappedCarriers.length;
    },
    excludedTotal() {
      return this.excludedCarriers.length;
    },
    excludedNames() {
      const names = this.excludedCarriers.map((c) => c && c.carrierName).filter(Boolean);
      if (names.length <= 3) return names.join(', ');
      return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
    },
    unmappedNames() {
      const names = this.unmappedCarriers
        .map((c) => c && c.carrierName)
        .filter(Boolean);
      if (names.length <= 3) return names.join(', ');
      return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
    },
  },
  methods: {
    onSelect(code) {
      // Toggle: clicking the active chip clears the filter.
      this.$emit('select-issue', this.activeIssue === code ? null : code);
    },
    chipStyle(chip) {
      return {
        background: chip.bg,
        borderColor: chip.active ? chip.color : chip.border,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.iv-action-panel {
  background: #ffffff;
  border: 1px solid #dfe4e3;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(12, 15, 14, 0.05);
  padding: 20px 22px;
  margin-bottom: 20px;
}

.iv-action-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 4px;
}

.iv-action-title {
  margin: 0;
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.02em;
  color: #0c0f0e;
}

.iv-action-tf {
  font-size: 12.5px;
  color: #9ca4a2;
}

.iv-action-desc {
  margin: 0 0 14px;
  font-size: 13.5px;
  color: #717977;
}

.iv-action-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;
}

.iv-action-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  text-align: left;
  transition: transform 0.1s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-1px);
  }
  &--active {
    box-shadow: 0 0 0 1px currentColor inset;
  }
}

.iv-action-chip-icon {
  display: flex;
  flex: none;
  font-size: 16px;
}

.iv-action-chip-label {
  font-size: 13.5px;
  font-weight: 600;
  color: #262b2a;
}

.iv-action-chip-count {
  margin-left: auto;
  font-weight: 800;
  font-size: 17px;
  font-variant-numeric: tabular-nums;
}

.iv-action-clear {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: #717977;

  .fa {
    color: #0f7259;
  }
}

// Same nudge idiom as .iv-unmapped below, one notch quieter (white card, amber
// rule) so two nudges stacked don't read as two alarms.
.iv-datafix {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  padding: 11px 14px;
  background: #ffffff;
  border: 1px solid #fde9c8;
  border-radius: 11px;
}

.iv-datafix-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #fef6e7;
  color: #b45309;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 14px;
}

.iv-datafix-title {
  font-weight: 700;
  font-size: 13.5px;
  color: #8a5a12;
}

.iv-datafix-sub {
  font-size: 12.5px;
  color: #717977;
}

// Same layout as .iv-unmapped, deliberately NOT its amber. An exclusion is a choice the
// office made, not a fault to fix.
.iv-excluded-note {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.iv-excluded-icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #94a3b8;
  color: #fff;
  font-size: 13px;
}

.iv-unmapped {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  padding: 11px 14px;
  background: #fef6e7;
  border: 1px solid #fde9c8;
  border-radius: 11px;
}

.iv-unmapped-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f59e0b;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 13px;
}

.iv-unmapped-title {
  font-weight: 700;
  font-size: 13.5px;
  color: #8a5a12;
}

.iv-unmapped-sub {
  font-size: 12.5px;
  color: #b45309;
}
</style>
